import { FC, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Paper,
  InputAdornment,
  Chip,
} from "@mui/material";
import {
  Add as AddIcon,
  Assignment as AssessmentIcon,
  Search as SearchIcon,
  School as SchoolIcon,
  Info as InfoIcon,
  Edit as EditIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useAcademicAssessmentsHook } from "../../../hooks/useAssessmentHook";
import { useClassHook, useSubjectsHook } from "../../../hooks/useAcademicHook";
import { pages, logger } from "../../../utils/common";
import ControlledTextField from "../../components/controlledComponents/ControlledTextField";
import ManageAcademicAssessmentDialog from "./manageAcademicAssessmentDialog";
import {
  academicAssessmentWireRefName,
  hasAssessmentPrerequisites,
} from "./assessment.helpers";

interface AcademicAssessmentsTabProps {
  isActive: boolean;
}

const AcademicAssessmentsTab: FC<AcademicAssessmentsTabProps> = ({
  isActive,
}) => {
  const navigate = useNavigate();
  const { data, isLoading, error } = useAcademicAssessmentsHook(undefined, {
    enabled: isActive,
  });
  const {
    data: classes,
    isLoading: classesLoading,
    error: classesError,
  } = useClassHook();
  const {
    data: subjects,
    isLoading: subjectsLoading,
    error: subjectsError,
  } = useSubjectsHook();

  const { control, watch } = useForm<{ search: string }>({
    defaultValues: { search: "" },
  });
  const searchQuery = watch("search");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingAssessmentId, setEditingAssessmentId] = useState<string | null>(
    null,
  );

  const prerequisitesMet = useMemo(
    () => hasAssessmentPrerequisites(classes, subjects),
    [classes, subjects],
  );
  const isLoadingPrerequisites = classesLoading || subjectsLoading;
  const hasPrerequisitesError = classesError || subjectsError;

  const rows = data?.data ?? [];
  const filtered = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => {
      const className = academicAssessmentWireRefName(r.class) ?? "";
      const courseName = academicAssessmentWireRefName(r.course) ?? "";
      return (
        r.name.toLowerCase().includes(q) ||
        r.assessmentType.toLowerCase().includes(q) ||
        r.status.toLowerCase().includes(q) ||
        className.toLowerCase().includes(q) ||
        courseName.toLowerCase().includes(q)
      );
    });
  }, [rows, searchQuery]);

  if (hasPrerequisitesError) {
    return (
      <Grid item xs={12} data-testid="academic-assessments-prerequisites-error">
        <Alert severity="error">
          Failed to load classes or subjects. Please try again later.
        </Alert>
      </Grid>
    );
  }

  if (isLoadingPrerequisites) {
    return (
      <Grid item xs={12} data-testid="academic-assessments-prerequisites-loading">
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      </Grid>
    );
  }

  const showPrerequisitesEmpty =
    !prerequisitesMet && filtered.length === 0 && rows.length === 0;

  if (showPrerequisitesEmpty) {
    return (
      <Grid item xs={12} data-testid="academic-assessments-prerequisites-empty">
        <Card>
          <CardContent>
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              minHeight="280px"
              gap={2}
            >
              <SchoolIcon sx={{ fontSize: 64, color: "text.secondary" }} />
              <Alert
                severity="info"
                icon={<InfoIcon />}
                sx={{ width: "100%", maxWidth: 560 }}
              >
                <Typography variant="h6" gutterBottom>
                  Set up classes and subjects first
                </Typography>
                <Typography variant="body2" paragraph>
                  Class assessments need a class and at least one institute subject.
                  Configure subjects and assign them to classes under Academics,
                  then return here.
                </Typography>
                <Button
                  variant="contained"
                  onClick={() => navigate(`${pages.academics.path}/subjects`)}
                  sx={{ mt: 1 }}
                  startIcon={<SchoolIcon />}
                  data-testid="academic-assessments-go-academic"
                >
                  Go to Academics
                </Button>
              </Alert>
            </Box>
          </CardContent>
        </Card>
      </Grid>
    );
  }

  if (error) {
    return (
      <Grid item xs={12} data-testid="academic-assessments-error">
        <Alert severity="error">
          Failed to load assessments. Please try again later.
        </Alert>
      </Grid>
    );
  }

  if (isLoading) {
    return (
      <Grid item xs={12} data-testid="academic-assessments-loading">
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      </Grid>
    );
  }

  return (
    <>
      <Grid item xs={12} data-testid="academic-assessments-header">
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          flexWrap="wrap"
          gap={2}
        >
          <Typography variant="h6" data-testid="academic-assessments-count">
            Class assessments ({filtered.length}
            {filtered.length !== rows.length ? ` of ${rows.length}` : ""})
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              setEditingAssessmentId(null);
              if (prerequisitesMet) {
                setDialogOpen(true);
                return;
              }
              logger.show_error(
                "Add classes and subjects in Academics before creating an assessment.",
              );
            }}
            disabled={!prerequisitesMet}
            data-testid="add-academic-assessment-button"
          >
            Create assessment
          </Button>
        </Box>
      </Grid>

      <Grid item xs={12}>
        <Card data-testid="academic-assessments-search-card">
          <CardContent>
            <ControlledTextField
              control={control}
              name="search"
              id="academic-assessments-search"
              label="Search"
              isGridItem={false}
              fullWidth
              placeholder="Search by name, type, status, class, subject…"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              inputProps={{ "data-testid": "academic-assessments-search-input" }}
            />
          </CardContent>
        </Card>
      </Grid>

      {filtered.length === 0 ? (
        <Grid item xs={12} data-testid="academic-assessments-empty">
          <Card>
            <CardContent>
              <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                py={4}
                gap={2}
              >
                <AssessmentIcon sx={{ fontSize: 56, color: "text.secondary" }} />
                <Typography variant="h6" color="text.secondary">
                  {rows.length === 0
                    ? "No assessments yet"
                    : "No assessments match your search"}
                </Typography>
                {rows.length === 0 && prerequisitesMet && (
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => {
                      setEditingAssessmentId(null);
                      setDialogOpen(true);
                    }}
                    data-testid="academic-assessments-empty-create"
                  >
                    Create assessment
                  </Button>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      ) : (
        <Grid item xs={12}>
          <TableContainer component={Paper} data-testid="academic-assessments-table-wrap">
            <Table size="small" data-testid="academic-assessments-table">
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Class</TableCell>
                  <TableCell>Course</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Max score</TableCell>
                  <TableCell align="right" sx={{ width: 56 }}>
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.map((a) => (
                  <TableRow key={a.id} data-testid={`academic-assessment-row-${a.id}`}>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {a.name}
                      </Typography>
                      {a.description && (
                        <Typography variant="caption" color="text.secondary">
                          {a.description}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {academicAssessmentWireRefName(a.class) ?? "—"}
                    </TableCell>
                    <TableCell>
                      {academicAssessmentWireRefName(a.course) ?? "—"}
                    </TableCell>
                    <TableCell>
                      <Chip label={a.assessmentType} size="small" variant="outlined" />
                    </TableCell>
                    <TableCell>
                      <Chip label={a.status} size="small" />
                    </TableCell>
                    <TableCell align="right">{a.maxScore}</TableCell>
                    <TableCell align="right">
                      <Tooltip title="Edit assessment">
                        <IconButton
                          size="small"
                          aria-label="Edit assessment"
                          onClick={() => {
                            setEditingAssessmentId(a.id);
                            setDialogOpen(true);
                          }}
                          data-testid={`academic-assessment-edit-${a.id}`}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>
      )}

      <ManageAcademicAssessmentDialog
        open={dialogOpen}
        onClose={() => {
          setDialogOpen(false);
          setEditingAssessmentId(null);
        }}
        editAssessmentId={editingAssessmentId}
      />
    </>
  );
};

export default AcademicAssessmentsTab;
